class ProductSelector extends HTMLElement {
  constructor() {
    super();
    this.inputWrapper = '[data-input-wrapper]';
    this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.form = document.querySelector(
      `#ProductForm-${this.dataset.sectionId}`
    );
    this.purchaseOptions = document.querySelector(
      `#Product-Purchase-Options-${this.dataset.sectionId}`
    );
    this.productBar = document.querySelector(
      `#ProductBar-${this.dataset.sectionId}`
    );
    this.installmentsForm = document.querySelector(
      `#Product-Installments-${this.dataset.installmentsFormId}`
    );
    this.selectorRefreshAbortController = null;
    this.selectorRefreshRequestId = 0;
    this.requiresCompleteSelection = this.hasAttribute(
      'data-require-complete-selection'
    );

    this.addEventListener('change', this.onVariantChange.bind(this));

    this.updateOptions();
    this.updateVariantStatuses();
    this.updateVariant();
    this.updateSelectionRequiredState();
    this.initUpdateMedia();

    if (this.currentVariant) {
      this.form.toggleAddButton(
        !this.currentVariant.available,
        this.currentVariant.available ? '' : window.theme.strings.soldOut
      );
    } else if (this.requiresCompleteSelection) {
      this.form.toggleAddButton(true, '');
    }
    this.createDropdownFromButtons();

    this.enableDropdownStyle();
  }

  disconnectedCallback() {
    if (this.outsideClickHandler) {
      window.removeEventListener('click', this.outsideClickHandler);
    }
    if (this.escapeKeyHandler) {
      window.removeEventListener('keydown', this.escapeKeyHandler);
    }
    this.selectorRefreshAbortController?.abort();
  }

  updateVariantStatuses() {
    const inputWrappers = [
      ...this.querySelectorAll(this.inputWrapper)
    ];

    inputWrappers.forEach((optionWrapper, optionIndex) => {
      const isColorSwatchWrapper = optionWrapper.classList.contains(
        'product-options--image-swatches'
      );

      // get selected values from other options
      const selectedValues = inputWrappers.map((wrapper, idx) => {
        if (idx === optionIndex) return null; // Bu option serbest
        const checked = wrapper.querySelector(':checked');
        return checked ? checked.value : null;
      });

      // get possible values for this option
      const possibleValues = this.variants
        .filter(variant => {
          // Color swatches are navigation as well as variant inputs. Keep every
          // color clickable, including colors whose sizes are all sold out.
          if (isColorSwatchWrapper) return true;

          return selectedValues.every((val, idx) => {
            if (val === null) return true;
            return variant[`option${idx + 1}`] === val;
          }) && variant.available;
        })
        .map(variant => variant[`option${optionIndex + 1}`]);

      const optionInputs = [
        ...optionWrapper.querySelectorAll('input[type="radio"], option')
      ];

      optionInputs.forEach(input => {
        const value = input.getAttribute('value');
        const isAvailable = possibleValues.includes(value);
        if (input.getAttribute('data-stock')) {
          return;
        }

        this.setOptionAvailability(input, isAvailable);

        if (input.tagName.toLowerCase() === 'option') {
          const escapedValue = input.value.replace(/"/g, '\"');
          const selector = `button[data-value="${escapedValue}"]`;
          const button = input.closest('dropdown-input').querySelector(selector);
          if (button) {
            button.disabled = !isAvailable;
          }
        }
        if (!this.productBar) return;
        const inputId = input.dataset.id || input.id;
        const productBarInput = this.productBar.querySelector(
          `option[data-id="${inputId}"]`
        );
        if (!productBarInput) return;
        productBarInput.disabled = !isAvailable;
      });
    });
  }

  setOptionAvailability(input, isAvailable) {
    const isSoldOut = !isAvailable;
    const optionItem = input.closest('.product-options__item');
    const baseAriaLabel = input.dataset.availableAriaLabel || input.getAttribute('aria-label') || input.value;

    input.dataset.availableAriaLabel = baseAriaLabel;
    input.disabled = isSoldOut;
    input.setAttribute(
      'aria-label',
      isSoldOut ? `${baseAriaLabel} — ${window.theme.strings.soldOut}` : baseAriaLabel
    );

    optionItem?.classList.toggle('is-sold-out', isSoldOut);
  }

  onVariantChange(event) {
    if (event.target.type === 'number') return;

    if (event.target.closest('.product-options--image-swatches')) {
      this.clearUnavailableSizeSelection();
    }

    const selectedOptionValues = Array.from(this.querySelectorAll('input[type="radio"]:checked')).map(({ dataset }) => dataset.optionValue);

    this.refreshSelector(selectedOptionValues);

    this.updateOptions();
    this.updateVariantStatuses();
    this.updateSelectionRequiredState(true);
    this.updateDropdownTriggers();
    if (this.currentVariant === this.getVariantData()) return;

    this.updateVariant();
    this.productBarUpdateOptions();
    this.updatePickupAvailability();
    if (this.form) {
      // this.form.removeAttribute('data-has-selling-plan'); // removing this is causing issue for selling plans
      this.form.toggleAddButton(false, '');
      this.form.handleErrorMessage();
    }

    if (!this.currentVariant) {
      if (this.form) {
        this.form.toggleAddButton(true, '');
      }
      if (!this.isSelectionIncomplete()) {
        this.setUnavailable();
      }

      return;
    }

    if (!this.currentVariant.available) {
      if (this.form) {
        this.form.toggleAddButton(true, window.theme.strings.soldOut);
      }
    }

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.renderProductInfo();
  }

  clearUnavailableSizeSelection() {
    const inputWrappers = [...this.querySelectorAll(this.inputWrapper)];
    const colorWrapper = this.querySelector('.product-options--image-swatches');
    const sizeWrapper = this.querySelector('.product-options--size-dropdown');
    const selectedColor = colorWrapper?.querySelector('input[type="radio"]:checked');
    const selectedSize = sizeWrapper?.querySelector('input[type="radio"]:checked');

    if (!selectedColor || !selectedSize) return;

    const selectedValues = inputWrappers.map(wrapper =>
      wrapper.querySelector('input[type="radio"]:checked')?.value || null
    );
    const selectionIsAvailable = this.variants.some(variant =>
      variant.available && selectedValues.every((value, index) =>
        value === null || variant[`option${index + 1}`] === value
      )
    );

    if (selectionIsAvailable) return;

    selectedSize.checked = false;
    selectedSize
      .closest('.product-options__item')
      ?.classList.remove('is-active');
  }

  refreshSelector(selectedOptionValues) {
    if (this.selectorRefreshAbortController) {
      this.selectorRefreshAbortController.abort();
    }

    const abortController = new AbortController();
    const requestId = ++this.selectorRefreshRequestId;
    this.selectorRefreshAbortController = abortController;

    const params =
      selectedOptionValues.length > 0
        ? `&option_values=${selectedOptionValues.join(',')}`
        : '';
    fetch(`${window.location.pathname}?${params}`, {
      signal: abortController.signal
    })
      .then(response => response.text())
      .then(responseText => {
        if (
          abortController.signal.aborted ||
          requestId !== this.selectorRefreshRequestId ||
          !this.isConnected
        ) {
          return;
        }

        const html = new DOMParser().parseFromString(responseText,'text/html');
        const newSelector = html.querySelector('product-selector');
        if (newSelector) {
          // Shopify can auto-select the first available variant when only a
          // color is requested. Preserve the shopper's exact partial selection
          // so an incompatible size stays cleared until they choose a new one.
          const selectedOptionValueSet = new Set(
            selectedOptionValues.map(String)
          );
          newSelector
            .querySelectorAll('input[type="radio"]')
            .forEach(input => {
              const isSelected = selectedOptionValueSet.has(
                input.dataset.optionValue
              );
              input.checked = isSelected;
              input.toggleAttribute('checked', isSelected);
            });

          this.replaceWith(newSelector);
        }
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.error('Unable to refresh product selector:', error);
        }
      });
  }

  updateOptions() {
    this.querySelectorAll('input[type="radio"]').forEach(input => {
      input.closest('.product-options__item')?.classList.toggle('is-active', input.checked);
    });
    this.options = Array.from(
      this.querySelectorAll('input[type="radio"]:checked, select'),
      el => ({ name: el.dataset.name || el.name, value: el.value })
    );
  }

  isSelectionIncomplete() {
    if (!this.requiresCompleteSelection) return false;

    return [...this.querySelectorAll(this.inputWrapper)].some(
      wrapper => !wrapper.querySelector('input[type="radio"]:checked')
    );
  }

  updateSelectionRequiredState(updateUrl = false) {
    const isIncomplete = this.isSelectionIncomplete();
    const variantInput = this.querySelector('input[name="id"]');

    this.toggleAttribute('data-selection-incomplete', isIncomplete);
    this.form?.classList.toggle(
      'is-variant-selection-incomplete',
      isIncomplete
    );

    if (isIncomplete && variantInput) {
      variantInput.value = '';
    }

    if (isIncomplete && updateUrl) {
      this.updateIncompleteSelectionURL();
    }
  }

  updateIncompleteSelectionURL() {
    const selectedOptionValues = Array.from(
      this.querySelectorAll('input[type="radio"]:checked'),
      input => input.dataset.optionValue
    ).filter(Boolean);
    const params = new URLSearchParams(window.location.search);

    params.delete('variant');
    if (selectedOptionValues.length > 0) {
      params.set('option_values', selectedOptionValues.join(','));
    } else {
      params.delete('option_values');
    }

    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}${query ? `?${query}` : ''}`
    );
  }

  updateDropdownTriggers() {
    this.querySelectorAll('.js-product-options-dropdown-trigger').forEach(
      trigger => {
        const wrapper = trigger.closest('.js-product-options-value-list');
        const checkedInput = wrapper?.querySelector(
          'input[type="radio"]:checked'
        );
        const selectedLabel = trigger.querySelector('[data-selected-label]');

        if (selectedLabel) {
          selectedLabel.textContent = checkedInput?.value || 'Select a size';
        }
      }
    );
  }

  productBarUpdateOptions() {
    if (this.productBar) {
      Array.from(this.productBar.querySelectorAll('select')).map(
        selector => {
          const selectedOption = this.options.find(
            option => option.name === selector.name
          );
          selector.value = selectedOption?.value || '';
        }
      );
      this.productBar.querySelectorAll('dropdown-input').forEach(
        dropdown => {
          dropdown.update();
        }
      );
    }
  }

  updateInventoryNotice(html) {
    const destination = document.querySelector('[data-inventory-notice]');
    const source = html.querySelector('[data-inventory-notice]');

    if (source && destination) destination.innerHTML = source.innerHTML;

    // Pass the inventory quantity from source to the quantity input
    const inventoryQuantityMatch = source?.textContent.match(/\d+/);
    const inventoryQuantity = inventoryQuantityMatch ? parseInt(inventoryQuantityMatch[0]) : 0;
    this.updateQuantityInput(inventoryQuantity);
  }

  updateQuantityInput(inventoryQuantity) {
    const selectedVariantData = this.getVariantData();
    const quantityInput = document.querySelector('.product-selector__quantity quantity-input input');

    let productUsesInventory = true;

    // check inventory is active
    if (selectedVariantData && selectedVariantData.inventory_management === null) {
      productUsesInventory = false;
    }

    // check sell on sold out is active
    if (selectedVariantData && selectedVariantData.inventory_quantity !== null && inventoryQuantity === 0 && selectedVariantData.available === true) {
      productUsesInventory = false;
    }

    if (quantityInput) {
      if (productUsesInventory) {
        quantityInput.setAttribute('max', inventoryQuantity);
        // set value accordingly to inventory
        if (quantityInput.value > inventoryQuantity) {
          quantityInput.value = inventoryQuantity;
        }
      } else {
        quantityInput.setAttribute('max', '');
      }
    }
  }

  updatePickupAvailability() {
    const pickUpAvailabilityNode = document.querySelector(
      'pickup-availability'
    );

    if (!pickUpAvailabilityNode) return;

    if (!this.currentVariant) {
      pickUpAvailabilityNode.removeAttribute('available');
      pickUpAvailabilityNode.innerHTML = '';
      return;
    }

    pickUpAvailabilityNode.dataset.variantId = this.currentVariant.id;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailabilityNode.setAttribute('available', '');
      pickUpAvailabilityNode.fetchAvailability(
        pickUpAvailabilityNode.dataset.variantId
      );

      return;
    }

    pickUpAvailabilityNode.removeAttribute('available');
    pickUpAvailabilityNode.innerHTML = '';
  }

  updateVariant() {
    this.currentVariant = this.getVariantData();
  }

  getVariantData() {
    if (this.isSelectionIncomplete()) {
      return undefined;
    }

    if (this.options?.length === 0) {
      return this.variants[0]; // Return the first variant if no options are selected
    }

    return this.variants.find(variant => {
      // Check if the variant's options match the selected options
      return this.options?.every(option => {
        const optionIndex = this.options.findIndex(opt => opt.name === option.name) + 1;
        return variant[`option${optionIndex}`] === option.value;
      });
    });
  }

  initUpdateMedia() {

    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = parseInt(urlParams.get('variant'));

    if (variantIdFromUrl) {
      this.currentVariant = this.variants.find(variant => variant.id === variantIdFromUrl);

      if (this.currentVariant) {
        this.updateMedia();
      }
    }
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media)
      return;

    const productMedia = document.querySelector('product-media');
    productMedia.setActiveMedia(
      this.currentVariant.featured_media.id
    );
  }

  updateURL(sellingPlanId) {
    if (!this.currentVariant) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('option_values');
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    if (sellingPlanId) {
      params[params.has('selling_plan') ? 'set' : 'append'](
        'selling_plan',
        sellingPlanId
      );
    } else {
      params.delete('selling_plan');
    }
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?${params.toString()}`
    );
  }

  updateVariantInput() {
    const inputs = [
      this.querySelector('[name="id"]'),
      this.installmentsForm?.querySelector('[name="id"]')
    ];
    inputs.forEach(input => {
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  setUnavailable() {
    const submitButton = this.form.submitButton;
    const price = this.querySelector(
      `#Product-Price-${this.dataset.sectionId}`
    );

    if (!submitButton) return;
    submitButton.textContent = window.theme.strings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  createDropdownFromButtons() {
    const inputWrappers = this.querySelectorAll(this.inputWrapper);

    inputWrappers.forEach((wrapper) => {
      const radios = wrapper.querySelectorAll('input[type="radio"]');
      const selectWrapper = wrapper.querySelector('[data-select-wrapper]');
      const select = wrapper.querySelector('[data-select]');

      if (!radios.length || !selectWrapper || !select) return;

      select.innerHTML = '';
      radios.forEach((radio) => {
        const option = document.createElement('option');
        option.value = radio.value;
        option.textContent = radio.value;
        if (radio.checked) option.selected = true;
        if (radio.disabled) option.disabled = true;
        select.appendChild(option);
      });

      select.addEventListener('change', (e) => {
        const selectedValue = e.target.value;

        // First uncheck all radios in this group
        radios.forEach(radio => {
          radio.checked = false;
          radio.closest('.product-options__item')?.classList.remove('is-active');
        });

        // Then check the selected one
        const targetRadio = Array.from(radios).find(r => r.value === selectedValue);
        if (targetRadio) {
          targetRadio.checked = true;
          targetRadio.closest('.product-options__item')?.classList.add('is-active');

          // Update the options state on the parent element
          this.updateOptions();

          // Dispatch the change event to trigger the variant change
          targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      selectWrapper.classList.remove('hidden');
    });
  }

  renderProductInfo() {
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    params.append('section_id', this.dataset.sectionId);

    fetch(`${this.dataset.url}?${params.toString()}`)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );
        [
          `#Product-Price-${this.dataset.sectionId}`,
          `#Product-Purchase-Options-${this.dataset.sectionId}`
        ].map(liveRegionSelector => {
          const destination = document.querySelector(
            liveRegionSelector
          );
          const source = html.querySelector(liveRegionSelector);

          this.updateInventoryNotice(html);

          if (destination && source) {
            destination.classList.remove('visibility-hidden');
            destination.innerHTML = source.innerHTML;
          }
        });
      });
  }

  enableDropdownStyle() {
    const wrappers = Array.from(this.querySelectorAll('.js-product-options-value-list'));

    wrappers.forEach(wrapper => {
      if (wrapper.closest('.product-selector--buttons')) return;
      wrapper.dataset.open = 'false';

      const dropdownTrigger = wrapper.querySelector(
        '.js-product-options-dropdown-trigger'
      );

      if (dropdownTrigger) {
        dropdownTrigger.addEventListener('click', event => {
          const isOpen = wrapper.dataset.open === 'true';
          event.stopPropagation();
          wrapper.dataset.open = isOpen ? 'false' : 'true';
          dropdownTrigger.setAttribute('aria-expanded', String(!isOpen));
        });

        wrapper.querySelector('ul')?.addEventListener('click', event => {
          const optionItem = event.target.closest('.product-options__item');
          const radioInput = optionItem?.querySelector('input[type="radio"]');

          if (!radioInput || radioInput.disabled) return;

          event.preventDefault();
          event.stopPropagation();

          wrapper.querySelectorAll('input[type="radio"]').forEach(radio => {
            radio.checked = false;
            radio.closest('.product-options__item')?.classList.remove('is-active');
          });

          radioInput.checked = true;
          optionItem.classList.add('is-active');
          wrapper.dataset.open = 'false';
          dropdownTrigger.setAttribute('aria-expanded', 'false');
          this.updateDropdownTriggers();
          radioInput.dispatchEvent(new Event('change', { bubbles: true }));
        });

        return;
      }

      if (
        wrapper.closest(
          '.product-options--image-swatches, .product-options--button-fallback'
        )
      ) {
        return;
      }

      wrapper.addEventListener('click', (e) => {
        const isOpen = wrapper.dataset.open === 'true';
        wrapper.dataset.open = isOpen ? 'false' : 'true';
        e.stopPropagation();

        // Handle selection when clicking on a product option item
        const target = e.target;
        const optionItem = target.closest('.product-options__item');

        if (optionItem) {
          const radioInput = optionItem.querySelector('input[type="radio"]');
          if (radioInput && !radioInput.checked) {
            // Uncheck all radios in this group first
            const allRadiosInGroup = wrapper.querySelectorAll('input[type="radio"]');
            allRadiosInGroup.forEach(radio => {
              radio.checked = false;
              radio.closest('.product-options__item')?.classList.remove('is-active');
            });

            // Check the selected radio
            radioInput.checked = true;
            optionItem.classList.add('is-active');

            // Update options and trigger change
            this.updateOptions();
            radioInput.dispatchEvent(new Event('change', { bubbles: true }));

            // Close dropdown after selection
            wrapper.dataset.open = 'false';
          }
        }
      });
    });

    this.updateDropdownTriggers();

    this.outsideClickHandler = event => {
      wrappers.forEach(wrapper => {
        if (!wrapper.contains(event.target)) {
          wrapper.dataset.open = 'false';
          wrapper
            .querySelector('.js-product-options-dropdown-trigger')
            ?.setAttribute('aria-expanded', 'false');
        }
      });
    };

    this.escapeKeyHandler = event => {
      if (event.key !== 'Escape') return;

      wrappers.forEach(wrapper => {
        wrapper.dataset.open = 'false';
        wrapper
          .querySelector('.js-product-options-dropdown-trigger')
          ?.setAttribute('aria-expanded', 'false');
      });
    };

    window.addEventListener('click', this.outsideClickHandler);
    window.addEventListener('keydown', this.escapeKeyHandler);
  }
}
customElements.define('product-selector', ProductSelector);
